import { assert } from '../../app/logger.js';
import { Pipeable } from './pipeable.js';

//
// Basic pipeline mechanism to connect data and data processors such as UI.
//
class PipeableObject extends Pipeable {
  pipe(map, { doNotNotify } = {}) {
    for (let property of Object.keys(map)) {
      let receiver = this.addReceiver(property, map[property]);
      if (!doNotNotify && property in this) {
        this.notifyReceiver(receiver, this[property]);
      }

      let baseProperties = this.dataDeps[property];
      if (baseProperties) {
        this.addDataDep(this, property, baseProperties);
      }
    }
    return this.createPipe(map);
  }

  unpipe(map) {
    for (let property of Object.keys(map)) {
      this.removeReceiver(property, map[property]);
      if (property in this.dataDeps) {
        this.removeDataDep(this, property);
      }
    }
  }

  set(data, { mergeStrategy } = {}) {
    assert(data, `[data] no data`);

    let oldPipedValues = this.pipedValues;

    switch (mergeStrategy) {
      case 'merge': {
        for (let property in data) {
          this.data[property] = data[property];
        }
        break;
      }
      default:
        this.data = data;
    }

    for (let [property, oldValue] of oldPipedValues.entries()) {
      this.notifyIfChanged(property, this[property], oldValue);
    }
    return this;
  }

  merge(data) {
    return this.set(data, {
      mergeStrategy: 'merge',
    });
  }

  setProperty(property, value) {
    let oldValue = this.data[property];
    if (oldValue == value) {
      return;
    }

    let deps = this.dataDepValues(property);
    this.data[property] = value;
    this.notify(property, value);

    for (let { object, property, oldValue } of deps) {
      object[`${property}Cached`] = undefined;
      object.notifyIfChanged(property, object[property], oldValue);
    }
  }

  // protected

  notifyIfChanged(property, value, oldValue) {
    if (value == oldValue) {
      return;
    }

    if (
      value &&
      oldValue &&
      typeof value === typeof oldValue &&
      typeof value == 'object'
    ) {
      let equal = true;
      for (let key in value) {
        if (value[key] != oldValue[key]) {
          equal = false;
          break;
        }
      }
      if (equal) {
        return;
      }
    }

    this.notify(property, value);
  }

  addDataDep(object, property, baseProperties) {
    assert(object instanceof Pipeable, `Not DataPipe`);
    assert(property && property != '', `No empty property`);
    assert(
      baseProperties instanceof Array && baseProperties.length > 0,
      `No empty array`
    );
    let index = this.findDataDepIndex(object, property);
    if (index == -1) {
      this.#deps.push({ object, property, baseProperties, refCount: 1 });
    } else {
      this.#deps[index].refCount++;
    }
  }

  removeDataDep(object, property) {
    let index = this.findDataDepIndex(object, property);
    assert(index != -1, `No ${property} dep found on ${object}`);
    if (index != -1) {
      if (--this.#deps[index].refCount == 0) {
        this.#deps.splice(index, 1);
      }
    }
  }

  findDataDepIndex(object, property) {
    return this.#deps.findIndex(
      i => i.object == object && i.property == property
    );
  }

  dataDepValues(property) {
    let depProperties = Array.from(Object.entries(this.dataDeps))
      .filter(([, baseProperties]) => baseProperties.includes(property))
      .map(([depProperty]) => depProperty);
    let allProperties = [property, ...depProperties];

    return this.#deps
      .filter(dep =>
        dep.baseProperties.find(property => allProperties.includes(property))
      )
      .map(dep => ({
        object: dep.object,
        property: dep.property,
        oldValue: dep.object[dep.property],
      }));
  }

  get dataDeps() {
    return this.constructor.dataDeps;
  }

  get depProperties() {
    return Object.keys(this.dataDeps);
  }

  get depPropertyValues() {
    return Array.from(this.depProperties).map(property => ({
      property,
      oldValue: this[property],
    }));
  }

  static dataDeps = {};
  static DefaultValue = {};

  #deps = [];
}

export { PipeableObject };
